import { supabase } from "../supabase-client";
import type { Database } from "../types/database";
import { withProfiles } from "./client_profiles";

export type Consultation = Database["public"]["Tables"]["consultations"]["Row"];
export type ConsultationInsert =
  Database["public"]["Tables"]["consultations"]["Insert"];
export type ConsultationUpdate =
  Database["public"]["Tables"]["consultations"]["Update"];

export interface ConsultationWithDetails extends Consultation {
  client_name?: string;
  client_email?: string;
  client_profile_image?: string;
  lawyer_name?: string;
  lawyer_email?: string;
}

/**
 * Create a new consultation request
 */
export async function createConsultation(
  consultationData: Omit<ConsultationInsert, "id" | "created_at">
) {
  if (consultationData.lawyer_id) {
    const { checkFeatureLimit } = await import('./subscriptions');
    const canCreate = await checkFeatureLimit(consultationData.lawyer_id, 'consultation');
    if (!canCreate) {
      throw new Error('Consultation creation limit reached for your current plan. Please upgrade to continue.');
    }
  }

  const { data, error } = await supabase
    .from("consultations")
    .insert(consultationData)
    .select()
    .single();

  if (error) throw error;
  return data;
}

/**
 * Get consultations by lawyer ID
 */
export async function getConsultationsByLawyer(
  lawyerId: string,
  options?: {
    status?: string;
    practice_area?: string;
    limit?: number;
  }
) {
  let query = supabase
    .from("consultations")
    .select(
      `
      *,
      client_profiles(
        id,
        client_type,
        phone,
        full_name,
        email,
        country
      )
    `
    )
    .eq("lawyer_id", lawyerId);

  if (options?.status) {
    query = query.eq("status", options.status);
  }

  if (options?.practice_area) {
    query = query.eq("practice_area", options.practice_area);
  }

  query = query.order("created_at", { ascending: false });

  if (options?.limit) {
    query = query.limit(options.limit);
  }

  const { data, error } = await query;

  if (error) throw error;
  return (data || []).map((c) => ({
    ...c,
    client_profiles: c.client_profiles ? withProfiles(c.client_profiles) : null,
  }));
}

/**
 * Get consultations by lawyer with advanced filters
 */
export async function getConsultationsByLawyerWithFilters(
  lawyerId: string,
  filters?: {
    status?:
      | "pending"
      | "accepted"
      | "rejected"
      | "completed"
      | "cancelled"
      | "all";
    practice_area?: string;
    urgency?: string;
    search?: string;
  }
) {
  let query = supabase
    .from("consultations")
    .select(
      `
      *,
      client_profiles(
        id,
        client_type,
        phone,
        full_name,
        email,
        country
      )
    `
    )
    .eq("lawyer_id", lawyerId);

  // Apply status filter
  if (filters?.status && filters.status !== "all") {
    query = query.eq("status", filters.status);
  }

  // Apply practice area filter
  if (filters?.practice_area) {
    query = query.eq("practice_area", filters.practice_area);
  }

  // Apply urgency filter
  if (filters?.urgency) {
    query = query.eq("urgency", filters.urgency);
  }

  const { data, error } = await query.order("created_at", {
    ascending: false,
  });

  if (error) throw error;

  const mapped = (data || []).map((c) => ({
    ...c,
    client_profiles: c.client_profiles ? withProfiles(c.client_profiles) : null,
  }));

  // Apply search filter on client side (Supabase doesn't support or with inner joins well)
  if (filters?.search && mapped.length > 0) {
    const searchLower = filters.search.toLowerCase();
    return mapped.filter((consultation) => {
      const description = consultation.description?.toLowerCase() || "";
      const clientName =
        consultation.client_profiles?.profiles?.full_name?.toLowerCase() || "";
      const clientEmail =
        consultation.client_profiles?.profiles?.email?.toLowerCase() || "";
      return (
        description.includes(searchLower) ||
        clientName.includes(searchLower) ||
        clientEmail.includes(searchLower)
      );
    });
  }

  return mapped;
}

/**
 * Get consultations by client ID
 */
export async function getConsultationsByClient(
  clientId: string,
  options?: {
    status?: string;
    limit?: number;
  }
) {
  let query = supabase
    .from("consultations")
    .select(
      `
      *,
      lawyer_profiles!inner(
        id,
        bar_number,
        university,
        years_of_experience,
        practice_areas,
        hourly_rate,
        is_verified,
        profiles!inner(
          full_name,
          email,
          profile_image_url,
          country
        )
      )
    `
    )
    .eq("client_id", clientId);

  if (options?.status) {
    query = query.eq("status", options.status);
  }

  query = query.order("created_at", { ascending: false });

  if (options?.limit) {
    query = query.limit(options.limit);
  }

  const { data, error } = await query;

  if (error) throw error;
  return data;
}

/**
 * Get consultation by ID
 */
export async function getConsultationById(consultationId: string) {
  const { data, error } = await supabase
    .from("consultations")
    .select(
      `
      *,
      client_profiles(
        id,
        client_type,
        phone,
        full_name,
        email,
        country
      ),
      lawyer_profiles(
        id,
        bar_number,
        university,
        years_of_experience,
        practice_areas,
        hourly_rate,
        phone,
        profiles(
          id,
          full_name,
          email,
          profile_image_url,
          country
        )
      )
    `
    )
    .eq("id", consultationId)
    .single();

  if (error) throw error;
  if (!data) return null;
  return {
    ...data,
    client_profiles: data.client_profiles
      ? withProfiles(data.client_profiles)
      : null,
  };
}

/**
 * Get pending consultations for a lawyer
 */
export async function getPendingConsultations(lawyerId: string) {
  const { data, error } = await supabase
    .from("consultations")
    .select(
      `
      *,
      client_profiles(
        id,
        client_type,
        phone,
        full_name,
        email,
        country
      )
    `
    )
    .eq("lawyer_id", lawyerId)
    .eq("status", "pending")
    .order("created_at", { ascending: false });

  if (error) throw error;
  return (data || []).map((c) => ({
    ...c,
    client_profiles: c.client_profiles ? withProfiles(c.client_profiles) : null,
  }));
}

/**
 * Get pending consultations count
 */
export async function getPendingConsultationsCount(
  lawyerId: string
): Promise<number> {
  const { count, error } = await supabase
    .from("consultations")
    .select("*", { count: "exact", head: true })
    .eq("lawyer_id", lawyerId)
    .eq("status", "pending");

  if (error) throw error;
  return count || 0;
}

/**
 * Accept consultation
 */
export async function acceptConsultation(
  consultationId: string,
  responseMessage?: string
) {
  const { data, error } = await supabase
    .from("consultations")
    .update({
      status: "accepted",
      response_message: responseMessage,
      responded_at: new Date().toISOString(),
    })
    .eq("id", consultationId)
    .select()
    .single();

  if (error) throw error;
  return data;
}

/**
 * Reject consultation
 */
export async function rejectConsultation(
  consultationId: string,
  responseMessage?: string
) {
  const { data, error } = await supabase
    .from("consultations")
    .update({
      status: "rejected",
      response_message: responseMessage,
      responded_at: new Date().toISOString(),
    })
    .eq("id", consultationId)
    .select()
    .single();

  if (error) throw error;
  return data;
}

/**
 * Complete consultation
 */
export async function completeConsultation(consultationId: string) {
  const { data, error } = await supabase
    .from("consultations")
    .update({
      status: "completed",
      completed_at: new Date().toISOString(),
    })
    .eq("id", consultationId)
    .select()
    .single();

  if (error) throw error;
  return data;
}

/**
 * Convert consultation to case
 */
export async function convertConsultationToCase(
  consultationId: string,
  caseId: string
) {
  const { data, error } = await supabase
    .from("consultations")
    .update({
      converted_to_case: true,
      case_id: caseId,
      status: "completed",
      completed_at: new Date().toISOString(),
    })
    .eq("id", consultationId)
    .select()
    .single();

  if (error) throw error;
  return data;
}

/**
 * Update consultation
 */
export async function updateConsultation(
  consultationId: string,
  updates: ConsultationUpdate
) {
  const { data, error } = await supabase
    .from("consultations")
    .update(updates)
    .eq("id", consultationId)
    .select()
    .single();

  if (error) throw error;
  return data;
}

/**
 * Get consultation statistics for a lawyer
 */
export async function getConsultationStats(lawyerId: string) {
  const { data, error } = await supabase
    .from("consultations")
    .select("status, practice_area")
    .eq("lawyer_id", lawyerId);

  if (error) throw error;

  const stats = {
    total: data.length,
    pending: 0,
    accepted: 0,
    rejected: 0,
    completed: 0,
    cancelled: 0,
    by_practice_area: {} as Record<string, number>,
  };

  data.forEach((consultation) => {
    // Count by status
    if (consultation.status === "pending") stats.pending++;
    else if (consultation.status === "accepted") stats.accepted++;
    else if (consultation.status === "rejected") stats.rejected++;
    else if (consultation.status === "completed") stats.completed++;
    else if (consultation.status === "cancelled") stats.cancelled++;

    // Count by practice area
    if (consultation.practice_area) {
      stats.by_practice_area[consultation.practice_area] =
        (stats.by_practice_area[consultation.practice_area] || 0) + 1;
    }
  });

  return stats;
}

/**
 * Search consultations
 */
export async function searchConsultations(
  lawyerId: string,
  searchTerm: string,
  options?: {
    status?: string;
    practice_area?: string;
  }
) {
  let query = supabase
    .from("consultations")
    .select(
      `
      *,
      client_profiles!inner(
        id,
        client_type,
        phone,
        full_name,
        email,
        country
      )
    `
    )
    .eq("lawyer_id", lawyerId);

  if (options?.status) {
    query = query.eq("status", options.status);
  }

  if (options?.practice_area) {
    query = query.eq("practice_area", options.practice_area);
  }

  // Search in description
  query = query.ilike("description", `%${searchTerm}%`);

  const { data, error } = await query.order("created_at", { ascending: false });

  if (error) throw error;
  return (data || []).map((c) => ({
    ...c,
    client_profiles: c.client_profiles ? withProfiles(c.client_profiles) : null,
  }));
}

/**
 * Get consultations that can be converted to cases (accepted ones)
 */
export async function getConsultationsForConversion(lawyerId: string) {
  const { data, error } = await supabase
    .from("consultations")
    .select(
      `
      *,
      client_profiles!inner(
        id,
        full_name,
        email,
        country
      )
    `
    )
    .eq("lawyer_id", lawyerId)
    .eq("status", "accepted")
    .eq("converted_to_case", false)
    .order("created_at", { ascending: false });

  if (error) throw error;
  return (data || []).map((c) => ({
    ...c,
    client_profiles: c.client_profiles ? withProfiles(c.client_profiles) : null,
  }));
}

/**
 * Get consultations by client with advanced filters
 */
export async function getClientConsultations(
  clientId: string,
  filters?: {
    status?:
      | "pending"
      | "accepted"
      | "rejected"
      | "completed"
      | "cancelled"
      | "all";
    practice_area?: string;
    urgency?: string;
    search?: string;
  }
) {
  let query = supabase
    .from("consultations")
    .select(
      `
      *,
      lawyer_profiles(
        id,
        bar_number,
        university,
        years_of_experience,
        practice_areas,
        hourly_rate,
        phone,
        profiles(
          id,
          full_name,
          email,
          profile_image_url,
          country
        )
      )
    `
    )
    .eq("client_id", clientId);

  // Apply status filter
  if (filters?.status && filters.status !== "all") {
    query = query.eq("status", filters.status);
  }

  // Apply practice area filter
  if (filters?.practice_area) {
    query = query.eq("practice_area", filters.practice_area);
  }

  // Apply urgency filter
  if (filters?.urgency) {
    query = query.eq("urgency", filters.urgency);
  }

  const { data, error } = await query.order("created_at", {
    ascending: false,
  });

  if (error) throw error;

  // Apply search filter on client side (Supabase doesn't support or with inner joins well)
  if (filters?.search && data) {
    const searchLower = filters.search.toLowerCase();
    return data.filter((consultation: any) => {
      const description = consultation.description?.toLowerCase() || "";
      const title = consultation.title?.toLowerCase() || "";
      const lawyerName =
        consultation.lawyer_profiles?.profiles?.full_name?.toLowerCase() || "";
      return (
        description.includes(searchLower) ||
        title.includes(searchLower) ||
        lawyerName.includes(searchLower)
      );
    });
  }

  return data;
}

/**
 * Get consultation statistics for a client
 */
export async function getClientConsultationStats(clientId: string) {
  const { data, error } = await supabase
    .from("consultations")
    .select("status, practice_area")
    .eq("client_id", clientId);

  if (error) throw error;

  const stats = {
    total: data.length,
    pending: 0,
    accepted: 0,
    rejected: 0,
    completed: 0,
    cancelled: 0,
    by_practice_area: {} as Record<string, number>,
  };

  data.forEach((consultation) => {
    // Count by status
    if (consultation.status === "pending") stats.pending++;
    else if (consultation.status === "accepted") stats.accepted++;
    else if (consultation.status === "rejected") stats.rejected++;
    else if (consultation.status === "completed") stats.completed++;
    else if (consultation.status === "cancelled") stats.cancelled++;

    // Count by practice area
    if (consultation.practice_area) {
      stats.by_practice_area[consultation.practice_area] =
        (stats.by_practice_area[consultation.practice_area] || 0) + 1;
    }
  });

  return stats;
}

/**
 * Cancel a consultation (only if pending and owned by client)
 */
export async function cancelConsultation(
  consultationId: string,
  clientId: string
) {
  // First verify ownership and status
  const { data: consultation, error: fetchError } = await supabase
    .from("consultations")
    .select("id, status, client_id")
    .eq("id", consultationId)
    .single();

  if (fetchError) throw fetchError;

  if (!consultation) {
    throw new Error("Consultation not found");
  }

  if (consultation.client_id !== clientId) {
    throw new Error(
      "Unauthorized: Consultation does not belong to this client"
    );
  }

  if (consultation.status !== "pending") {
    throw new Error("Only pending consultations can be cancelled");
  }

  // Update status to cancelled
  const { data, error } = await supabase
    .from("consultations")
    .update({ status: "cancelled" })
    .eq("id", consultationId)
    .eq("client_id", clientId)
    .select()
    .single();

  if (error) throw error;
  return data;
}
